import React from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import SeoHead from "../components/SeoHead";

const sections = [
  {
    title: "1. Introduction",
    content: (
      <>
        <p>
          Ghost Inbox (&quot;Ghost Inbox,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;)
          operates the website{" "}
          <a
            href="https://ghostinbox.xyz"
            className="text-emerald-400 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://ghostinbox.xyz
          </a>{" "}
          and related services (collectively, the &quot;Service&quot;). This Privacy Policy
          explains how we collect, use, disclose, and safeguard information when you use our
          privacy-focused temporary inbox platform.
        </p>
        <p className="mt-3">
          By accessing or using the Service, you acknowledge that you have read and understood
          this Privacy Policy. If you do not agree with our practices, please do not use the
          Service.
        </p>
      </>
    ),
  },
  {
    title: "2. Information We Collect",
    content: (
      <>
        <p className="font-medium text-zinc-300">Information collected</p>
        <ul className="mt-2 list-disc space-y-2 pl-5">
          <li>
            <strong className="text-zinc-300">Account information:</strong> When you register,
            we collect your full name, email address, and password. Passwords are stored using
            industry-standard one-way hashing and are never stored in plain text.
          </li>
          <li>
            <strong className="text-zinc-300">Inbox names:</strong> When you create or load a
            temporary inbox (for example,{" "}
            <span className="font-mono text-zinc-300">demo@ghostinbox.xyz</span>), we record the
            username portion you choose so it can be associated with your account.
          </li>
          <li>
            <strong className="text-zinc-300">Email metadata:</strong> We process metadata
            associated with inbound messages (such as sender, subject, and timestamps) to display
            messages in your dashboard during the retention window.
          </li>
          <li>
            <strong className="text-zinc-300">Contact form submissions:</strong> If you contact
            us through our Contact page, we collect your name, email address, and message content.
          </li>
        </ul>
        <p className="mt-4 font-medium text-zinc-300">Information collected automatically</p>
        <ul className="mt-2 list-disc space-y-2 pl-5">
          <li>
            <strong className="text-zinc-300">Usage and analytics data:</strong> We use Google
            Analytics to collect aggregated, anonymized information about how visitors interact
            with our website, such as pages viewed, session duration, and general device or
            browser type.
          </li>
          <li>
            <strong className="text-zinc-300">Technical logs:</strong> Our servers may automatically
            log standard information such as request timestamps, error logs, and diagnostic data
            for security, troubleshooting, and service reliability.
          </li>
          <li>
            <strong className="text-zinc-300">IP addresses:</strong> We may collect IP addresses for
            abuse prevention, rate limiting, security monitoring, and fraud detection.
          </li>
          <li>
            <strong className="text-zinc-300">Authentication tokens:</strong> When you sign in,
            a secure session token is stored in your browser to keep you logged in. You can end
            your session at any time by logging out.
          </li>
        </ul>
        <p className="mt-4 font-medium text-zinc-300">Temporary email content</p>
        <p className="mt-2">
          Messages sent to @ghostinbox.xyz addresses are received and held temporarily in our
          systems so you can view them in your dashboard. This content is described further in
          Section 4 below.
        </p>
      </>
    ),
  },
  {
    title: "3. How We Use Your Information",
    content: (
      <>
        <p>Information may be used to:</p>
        <ul className="mt-3 list-disc space-y-2 pl-5">
          <li>Deliver services, including inbox creation, message display, and auto-refresh.</li>
          <li>Create, authenticate, and manage your user account.</li>
          <li>Prevent abuse, fraud, and policy violations.</li>
          <li>Improve performance, reliability, and user experience.</li>
          <li>Maintain security through monitoring, rate limiting, and incident response.</li>
          <li>Respond to support requests and contact form inquiries.</li>
          <li>Enforce account restrictions, including blocking or removal of accounts where necessary.</li>
          <li>Analyze aggregated website usage through Google Analytics.</li>
          <li>Comply with applicable legal obligations and lawful requests from authorities.</li>
        </ul>
      </>
    ),
  },
  {
    title: "4. Temporary Email Data",
    content: (
      <>
        <p>
          Ghost Inbox is designed as a temporary inbox platform for testing and privacy protection.
          Email messages delivered to @ghostinbox.xyz addresses are stored temporarily in our
          infrastructure solely so you can read them in your inbox dashboard during the retention
          window.
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-5">
          <li>
            <strong className="text-zinc-300">Automatic deletion:</strong> Temporary inboxes and
            their messages are automatically deleted after approximately 20 minutes.
          </li>
          <li>
            <strong className="text-zinc-300">No long-term mailbox storage:</strong> We do not
            provide permanent email hosting or archival storage for received messages.
          </li>
          <li>
            <strong className="text-zinc-300">Account-linked inboxes:</strong> Temporary addresses
            are tied to registered accounts. Do not use Ghost Inbox for sensitive, confidential, or
            regulated information.
          </li>
          <li>
            <strong className="text-zinc-300">No guarantee of delivery:</strong> We do not
            guarantee that every message sent to a temporary address will be received, displayed,
            or retained for the full 20-minute window.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "5. Legal Bases for Processing (EEA/UK Users)",
    content: (
      <p>
        If you are located in the European Economic Area or the United Kingdom, we process
        personal data on the following bases: (a) <strong className="text-zinc-300">contractual necessity</strong> to
        provide the Service you request; (b) <strong className="text-zinc-300">legitimate interests</strong> in securing,
        operating, and improving our platform; (c) <strong className="text-zinc-300">consent</strong> where required,
        such as for non-essential analytics; and (d) <strong className="text-zinc-300">legal obligation</strong> where
        applicable law requires us to retain or disclose information.
      </p>
    ),
  },
  {
    title: "6. Cookies and Analytics",
    content: (
      <>
        <p>
          We use cookies and similar technologies to maintain login sessions and understand how
          the Service is used. Google Analytics may set cookies to collect anonymized traffic
          statistics. You can control cookies through your browser settings and, where available,
          through Google&apos;s analytics opt-out tools.
        </p>
        <p className="mt-3">
          Disabling cookies may limit certain features, including the ability to remain signed in
          to your account.
        </p>
      </>
    ),
  },
  {
    title: "7. How We Share Information",
    content: (
      <>
        <p>We do not sell your personal information. We may share information only in these circumstances:</p>
        <ul className="mt-3 list-disc space-y-2 pl-5">
          <li>
            <strong className="text-zinc-300">Service providers:</strong> With trusted vendors
            who help us operate the Service (for example, hosting, database, analytics, or email
            processing infrastructure), subject to confidentiality and data-protection obligations.
          </li>
          <li>
            <strong className="text-zinc-300">Legal compliance:</strong> When required by law,
            regulation, legal process, or enforceable governmental request.
          </li>
          <li>
            <strong className="text-zinc-300">Protection of rights:</strong> To investigate,
            prevent, or take action regarding suspected fraud, abuse, security threats, or
            violations of our Terms of Service.
          </li>
          <li>
            <strong className="text-zinc-300">Business transfers:</strong> In connection with a
            merger, acquisition, restructuring, or sale of assets, subject to continued protection
            of your information.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "8. Data Retention",
    content: (
      <>
        <p>
          Temporary emails are automatically deleted according to platform retention policies
          (approximately 20 minutes). Technical logs may be retained longer for security and
          compliance purposes.
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-5">
          <li>
            <strong className="text-zinc-300">Temporary emails:</strong> Deleted automatically after
            approximately 20 minutes. Storage is not guaranteed for the full retention window.
          </li>
          <li>
            <strong className="text-zinc-300">Account data:</strong> Retained while your account is
            active. If your account is deleted, associated account data is removed from our systems,
            though you may register again using the same email address.
          </li>
          <li>
            <strong className="text-zinc-300">Contact messages:</strong> Retained as long as needed
            to respond to your inquiry and for reasonable record-keeping, unless a longer period is
            required by law.
          </li>
          <li>
            <strong className="text-zinc-300">Technical logs and analytics:</strong> Retained for a
            limited period necessary for abuse prevention, security, troubleshooting, and statistical
            analysis.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "9. Data Security",
    content: (
      <>
        <p>
          We implement reasonable administrative, technical, and organizational measures designed
          to protect your information, including encrypted password storage, access controls, and
          secure server infrastructure. However, no method of transmission over the Internet or
          electronic storage is completely secure, and we cannot guarantee absolute security.
        </p>
        <p className="mt-3">
          You are responsible for maintaining the confidentiality of your account credentials and
          for all activity that occurs under your account.
        </p>
      </>
    ),
  },
  {
    title: "10. Your Rights and Choices",
    content: (
      <>
        <p>Depending on your location, you may have the right to:</p>
        <ul className="mt-3 list-disc space-y-2 pl-5">
          <li>Access the personal information we hold about you.</li>
          <li>Request correction of inaccurate or incomplete information.</li>
          <li>Request deletion of your account and associated personal data.</li>
          <li>Object to or restrict certain processing activities.</li>
          <li>Withdraw consent where processing is based on consent.</li>
          <li>Lodge a complaint with a data protection authority in your jurisdiction.</li>
        </ul>
        <p className="mt-3">
          To exercise these rights, please contact us through our{" "}
          <Link to="/contact" className="text-emerald-400 hover:underline">
            Contact Us
          </Link>{" "}
          page. We may need to verify your identity before fulfilling a request. If your account
          has been blocked for policy violations, you may contact us to appeal or request review.
        </p>
      </>
    ),
  },
  {
    title: "11. Children's Privacy",
    content: (
      <p>
        The Service is not directed to children under the age of 13 (or the minimum age required
        in your jurisdiction), and we do not knowingly collect personal information from children.
        If you believe a child has provided us with personal information, please contact us and we
        will take steps to delete such information.
      </p>
    ),
  },
  {
    title: "12. International Data Transfers",
    content: (
      <p>
        Your information may be processed and stored on servers located outside your country of
        residence, including in jurisdictions that may have different data-protection laws. Where
        required, we implement appropriate safeguards for cross-border transfers of personal data.
      </p>
    ),
  },
  {
    title: "13. Third-Party Links and Services",
    content: (
      <p>
        The Service may contain links to third-party websites or services that we do not operate.
        We are not responsible for the privacy practices of those third parties. We encourage you
        to review the privacy policies of any external site you visit.
      </p>
    ),
  },
  {
    title: "14. Changes to This Privacy Policy",
    content: (
      <p>
        We may update this Privacy Policy from time to time. When we do, we will revise the
        &quot;Last updated&quot; date at the top of this page. Material changes may also be
        communicated through the Service or by other appropriate means. Your continued use of
        Ghost Inbox after changes become effective constitutes acceptance of the updated policy.
      </p>
    ),
  },
  {
    title: "15. Contact Us",
    content: (
      <p>
        If you have questions, concerns, or requests regarding this Privacy Policy or our data
        practices, please reach out through our{" "}
        <Link to="/contact" className="text-emerald-400 hover:underline">
          Contact Us
        </Link>{" "}
        page at{" "}
        <a
          href="https://ghostinbox.xyz/contact"
          className="text-emerald-400 hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          ghostinbox.xyz/contact
        </a>
        .
      </p>
    ),
  },
];

function Privacy() {
  return (
    <Layout>
      <SeoHead
        title="Privacy Policy – Ghost Inbox"
        description="Ghost Inbox Privacy Policy. Learn how we collect and use inbox names, email metadata, technical logs, and IP addresses for service delivery, abuse prevention, and security."
        path="/privacy"
      />

      <div className="mx-auto max-w-3xl px-6 py-16">
        <p className="text-xs font-medium uppercase tracking-wider text-emerald-400">Legal</p>
        <h1 className="mt-2 text-3xl font-bold text-white">Privacy Policy</h1>
        <p className="mt-4 text-sm text-zinc-500">Last updated: 10 June 2026</p>
        <p className="mt-4 leading-relaxed text-zinc-400">
          This policy describes how Ghost Inbox collects, uses, and protects information when you
          use our temporary inbox platform at @ghostinbox.xyz.
        </p>

        <div className="mt-12 space-y-6">
          {sections.map((section) => (
            <section
              key={section.title}
              className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6"
            >
              <h2 className="text-lg font-semibold text-white">{section.title}</h2>
              <div className="mt-3 space-y-3 text-sm leading-relaxed text-zinc-400">
                {section.content}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 flex flex-col gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-zinc-400">
            Also read our{" "}
            <Link to="/terms" className="text-emerald-400 hover:underline">
              Terms of Service
            </Link>
            .
          </p>
          <Link
            to="/contact"
            className="inline-block rounded-lg border border-zinc-700 px-5 py-2.5 text-center text-sm text-zinc-300 hover:border-zinc-500"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </Layout>
  );
}

export default Privacy;
